import * as React from "react";

import AppContext from "../../AppContext";
import { ContentType, HttpVerb, requestWithAuth } from "../../../../utils/httpRequest";
import Partner from "../../../../classes/Partner";

import api from "../../../api";
import Company, { EnrichmentStatus } from "../../../../classes/Company";

import "./CompanySection.css";
import { openOdooUrl } from "../../../../mock/mockInterceptor";
import {
  faBuilding,
  faDollarSign,
  faGlobeEurope,
  faIndustry,
  faInfo,
  faKey,
  faMapMarkerAlt,
  faPersonBooth,
  faPhone
} from "@fortawesome/free-solid-svg-icons";
import CompanyInfoItem from "../CompanyInfoItem";
import CompanySocialIcons from "../CompanySocialIcons";
import CollapseSection from "../../CollapseSection/CollapseSection";
import CompanyCache from "../../../../classes/CompanyCache";
import EnrichmentInfo, { EnrichmentInfoType } from "../../../../classes/EnrichmentInfo";
import { Spinner, SpinnerSize } from "office-ui-fabric-react";
import { OdooTheme } from "../../../../utils/Themes";
import { _t } from "../../../../utils/Translator";

type CompanySectionProps = {
  partner: Partner;
  canCreatePartner: boolean;
  onPartnerInfoChanged?: (partner: Partner) => void;
  hideCollapseButton: boolean;
};
type CompanySectionState = {
  company: Company;
  isCollapsed: boolean;
  isLoading: boolean;
  hideEnrichmentButton: boolean;
};

const defaultCompanyImageSrc = "assets/company_image.png";

class CompanySection extends React.Component<CompanySectionProps, CompanySectionState> {
  private companyCache: CompanyCache;
  
  constructor(props, context) {
    super(props, context);
    
    // Initialize companyCache before using it
    this.companyCache = new CompanyCache(2, 200, 0.25);

    const partnerEmail = typeof props.partner.email === "string" ? props.partner.email : undefined;
    if ((!props.partner.company || props.partner.company.id === -1) && partnerEmail && partnerEmail.includes("@")) {
      const cachedCompany = this.companyCache.get(partnerEmail);
      if (cachedCompany) {
        props.partner.company = cachedCompany;
      }
    }

    // Don't fetch company if partner doesn't exist in database (id: -1)
    if (props.partner.id === -1) {
      // Partner not in database, skip company fetch - will set company to null below
    } else if ((!props.partner.company || props.partner.company.id === -1) && props.partner.companyId) {
      // Ensure companyId is a valid number and not -1
      const companyId =
        typeof props.partner.companyId === "number" ? props.partner.companyId : parseInt(String(props.partner.companyId));
      // Normalize partner ID for comparison
      const partnerIdNum = typeof props.partner.id === 'number' ? props.partner.id : parseInt(String(props.partner.id));
      
      // Don't fetch if companyId is the same as partner's own ID (contact pointing to itself)
      // Only fetch if companyId is valid, not -1, and different from partner's own ID
      if (companyId && !isNaN(companyId) && companyId > 0 && companyId !== -1 && companyId !== partnerIdNum) {
        if (!props.partner.company || props.partner.company.id === -1) {
          // Safety check: Never make API call with id: -1
          if (companyId === -1 || companyId <= 0) {
            return;
          }
          const companyRequest = requestWithAuth(
            HttpVerb.PATCH,
            api.baseURL + api.getPartner.replace("{id}", companyId.toString()),
            ContentType.Json
          );

          this.context.addRequestCanceller(companyRequest.cancel);

          companyRequest.promise
            .then(({ responseText }) => {
              const parsed = JSON.parse(responseText);
              
              // Normalize Odoo boolean values
              const normalizeBoolean = (value: any): boolean => {
                if (value === true || value === 'True' || value === 'true' || value === 1) return true;
                if (value === false || value === 'False' || value === 'false' || value === 0) return false;
                return Boolean(value);
              };
              
              const isSuccess = normalizeBoolean(parsed.success);
              
              // Check for error response from backend
              if (!isSuccess && parsed.error) {
                this.context.showHttpErrorMessage(parsed);
                return;
              }
              
              if (isSuccess && parsed.data) {
                const company = Company.fromJSON(parsed.data);
                if (company?.getName()) {
                  this.companyCache.add(company);
                  const newPartner = this.props.partner;
                  newPartner.company = company;
                  this.props.onPartnerInfoChanged(newPartner);
                  this.setState({ company: company });
                } else {
                  this.context.showHttpErrorMessage(new Error(_t("Company details could not be retrieved.")));
                }
              } else {
                this.context.showHttpErrorMessage(new Error(_t("Company details could not be retrieved.")));
              }
            })
            .catch(error => {
              this.context.showHttpErrorMessage(error);
            });
        }
      }
    }

    // Only set company in state if it's actually valid (not empty and contact has companyId or is a company)
    let company = props.partner.company;
    if (props.partner.id === -1) {
      // Partner not in database, don't fetch or show company info
      company = null;
    } else if (!props.partner.isCompany && !props.partner.companyId) {
      // Contact is a person without a company - don't set company in state
      company = null;
    } else if (company && company.id === -1 && !props.partner.companyId) {
      // Company is empty and contact has no companyId - don't set company in state
      company = null;
    }
    
    this.state = {
      company: company,
      isCollapsed: false,
      isLoading: false,
      hideEnrichmentButton: false
    };
  }

  // private enrichAndCreate = () => {
  //     if (!this.props.partner.email) {
  //         const enrichmentInfo = new EnrichmentInfo(
  //             EnrichmentInfoType.EnrichContactWithNoEmail,
  //             _t('This company has no email address and could not be enriched.'),
  //         );
  //         this.context.showTopBarMessage(enrichmentInfo);
  //         return;
  //     }

  //     this.setState({ isLoading: true });
  //     const enrichRequest = sendHttpRequest(
  //         HttpVerb.POST,
  //         api.baseURL + api.createCompany,
  //         ContentType.Json,
  //         this.context.getConnectionToken(),
  //         {
  //             partner_email: this.props.partner.email,
  //             partner_id: this.props.partner.id,
  //             enrich_on_not_exists: true,
  //         },
  //     );
  //     this.context.addRequestCanceller(enrichRequest.cancel);
  //     enrichRequest.promise
  //         .then((response) => {
  //             const enrichResponse = JSON.parse(response);
  //             if (enrichResponse.result['enrichment_info']) {
  //                 const enrichmentInfo = new EnrichmentInfo(
  //                     enrichResponse.result['enrichment_info'].type,
  //                     enrichResponse.result['enrichment_info'].info,
  //                 );
  //                 this.context.showTopBarMessage(enrichmentInfo);
  //             } else if (enrichResponse.result.error && enrichResponse.result.error.length) {
  //                 this.context.showTopBarMessage(
  //                     new EnrichmentInfo(EnrichmentInfoType.OdooCustomError, enrichResponse.result.error),
  //                 );
  //                 this.setState({ isLoading: false, hideEnrichmentButton: true });
  //                 return;
  //             }

  //             const company = Company.fromJSON(enrichResponse.result.company);
  //             if (company.enrichmentStatus == EnrichmentStatus.enriched) {
  //                 this.companyCache.add(company);
  //             }
  //             this.setState({ isCollapsed: false, company: company, isLoading: false });
  //             const newPartner = this.props.partner;
  //             newPartner.company = company;
  //             this.props.onPartnerInfoChanged(newPartner);
  //         })
  //         .catch((error) => {
  //             this.setState({ isCollapsed: false, isLoading: false, hideEnrichmentButton: true });
  //             this.context.showHttpErrorMessage(error);
  //         });
  // };

  private enrichAndUpdate = () => {
    if (!this.props.partner.email) {
      let enrichmentInfo = new EnrichmentInfo(
        EnrichmentInfoType.EnrichContactWithNoEmail,
        _t("This company has no email address and it could not be enriched.")
      );
      this.context.showTopBarMessage(enrichmentInfo);
      return;
    }

    this.setState({ isLoading: true });

    const enrichRequest = requestWithAuth(
      HttpVerb.POST,
      api.baseURL + api.enrichAndUpdate,
      ContentType.Json,
      {
        partner_id: this.props.partner.company.id
      }
    );

    this.context.addRequestCanceller(enrichRequest.cancel);

    enrichRequest.promise
      .then(({ responseText }) => {
        const enrichResponse = JSON.parse(responseText);

        let enrichmentInfo = null;

        if (enrichResponse.result["enrichment_info"]) {
          enrichmentInfo = new EnrichmentInfo(
            enrichResponse.result["enrichment_info"].type,
            enrichResponse.result["enrichment_info"].info
          );
          this.context.showTopBarMessage(enrichmentInfo);
        } else if (enrichResponse.result.error && enrichResponse.result.error.length) {
          this.context.showTopBarMessage(
            new EnrichmentInfo(EnrichmentInfoType.OdooCustomError, enrichResponse.result.error)
          );
          this.setState({ isLoading: false, hideEnrichmentButton: true });
          return;
        }
        const partner = this.props.partner;
        const company = Company.fromJSON(enrichResponse.result.company, enrichmentInfo);
        this.setState({ company: company, isLoading: false, isCollapsed: false });
        //update partner information
        partner.company = company;
        partner.image = company.image;
        partner.phone = company.phone;
        this.props.onPartnerInfoChanged(partner);
      })
      .catch(error => {
        this.setState({ isCollapsed: false, isLoading: false, hideEnrichmentButton: true });
        this.context.showHttpErrorMessage(error);
      });
  };

  private getCompanyInsightsSection = () => {
    // Don't show insights if contact is a person without a company attached
    if (!this.props.partner.isCompany && !this.props.partner.companyId) {
      return null;
    }
    
    const company = Array.isArray(this.state.company)
      ? (this.state.company[0] as Company)
      : (this.state.company as Company);
    
    // Don't show insights if company is empty or invalid
    if (!company || company.id === -1 || (company.id === this.props.partner.id && !this.props.partner.isCompany)) {
      return null;
    }
    
    const parentIsCompany = this.props.partner.isCompany;

    const companyInsights = [];

    const companyLocation = company?.getLocation();
    if (companyLocation) {
      companyInsights.push(
        <CompanyInfoItem
          key="address"
          icon={faMapMarkerAlt}
          title={_t("Address")}
          value={companyLocation}
          hrefContent={`http://maps.google.com/?q=${companyLocation}`}
        />
      );
    }

    const companyPhone = company?.getPhone?.();
    if (companyPhone && !parentIsCompany) {
      companyInsights.push(
        <CompanyInfoItem key="phone" icon={faPhone} title={_t("Phone")} value={companyPhone} hrefContent={`tel:${companyPhone}`} />
      );
    }

    const companyDomain = company.getDomain();
    if (companyDomain) {
      companyInsights.push(
        <CompanyInfoItem key="website" icon={faGlobeEurope} title={_t("Website")} value={companyDomain} hrefContent={companyDomain} />
      );
    }

    const companyIndustry = company.getIndustry();
    if (companyIndustry) {
      companyInsights.push(<CompanyInfoItem key="industry" icon={faIndustry} title={_t("Industry")} value={companyIndustry} />);
    }

    const companyEmployees = company.getEmployees();
    if (companyEmployees) {
      companyInsights.push(
        <CompanyInfoItem key="employees" icon={faPersonBooth} title={_t("Employees")} value={companyEmployees + ""} />
      );
    }

    const companyYearFounded = company.getYearFounded();
    if (companyYearFounded) {
      companyInsights.push(
        <CompanyInfoItem key="year-founded" icon={faInfo} title={_t("Year founded")} value={"" + company.getYearFounded()} />
      );
    }

    const companyKeywords = company.getKeywords();
    if (companyKeywords) {
      companyInsights.push(<CompanyInfoItem key="keywords" icon={faKey} title={_t("Keywords")} value={companyKeywords} />);
    }

    const companyType = company.getCompanyType();
    if (companyType) {
      companyInsights.push(<CompanyInfoItem key="company-type" icon={faBuilding} title={_t("Company Type")} value={companyType} />);
    }

    const companyRevenue = company.getRevenue();
    if (companyRevenue) {
      companyInsights.push(<CompanyInfoItem key="revenue" icon={faDollarSign} title={_t("Revenues")} value={companyRevenue} />);
    }

    if (!companyInsights.length && parentIsCompany) {
      if (company.enrichmentStatus != EnrichmentStatus.enrichmentEmpty) {
        return <div className="insights-empty-info muted-text">{_t("No extra information found")}</div>;
      } else {
        return null;
      }
    }

    const icon = company.image ? Partner.getImageDataUrl(company.image) : company.getLogoURL() || defaultCompanyImageSrc;

    return (
      <>
        <div className="company-header" onClick={this.openInOdoo}>
          {!parentIsCompany && (
            <div className="company-name">
              <img
                src={icon}
                onError={ev => {
                  ev.currentTarget.src = defaultCompanyImageSrc;
                }}
              />
              <span>{company.getName()}</span>
            </div>
          )}
          <CompanySocialIcons
            twitter={company.getTwitter()}
            facebook={company.getFacebook()}
            linkedin={company.getLinkedin()}
            crunchbase={company.getCrunchbase()}
          />
        </div>
        <div className="company-insights">
          <span className="company-description">{company.getDescription()}</span>
          {companyInsights}
        </div>
      </>
    );
  };

  private getCompanyEnrichmentAvailableSection = () => {
    const company = this.state.company;
    if (company && company.isAddedToDatabase()) {
      return (
        <div>
          {this.getCompanyInsightsSection()}
          {!this.state.hideEnrichmentButton && this.props.canCreatePartner && (
            <div>
              <div className="odoo-secondary-button insights-button" onClick={this.enrichAndUpdate}>
                {_t("Enrich Company")}
              </div>
            </div>
          )}
        </div>
      );
    } else if (this.props.partner.isAddedToDatabase()) {
      return (
        <div className="muted-text insights-info">
          {_t("No company attached to this contact")}
          {/* {!this.state.hideEnrichmentButton && this.props.canCreatePartner && (
                        <div className="odoo-secondary-button insights-button" onClick={this.enrichAndCreate}>
                            {_t('Create a Company')}
                        </div>
                    )} */}
        </div>
      );
    } else if (this.props.canCreatePartner) {
      return <div className="muted-text insights-info">{_t("Save the contact to create the company")}</div>;
    }

    return null;
  };

  // private getCompanyEnrichmentEmptySection = () => {
  //     let message = null;
  //     if (!this.context.isConnected()) {
  //         message = _t('No company linked to this contact could be enriched');
  //     } else if (this.state.company.isAddedToDatabase()) {
  //         message = _t('No additional insights were found for this company');
  //     } else {
  //         message = _t('No company linked to this contact could be enriched or found in Odoo');
  //     }

  //     return (
  //         <div>
  //             {this.state.company.isAddedToDatabase() && this.getCompanyInsightsSection()}
  //             <div className="muted-text insights-info">{message}</div>
  //         </div>
  //     );
  // };

  //returns the right JSX element based on the enrichment status of the company
  private getCompanySection = () => {
    // If contact is a person without a company attached, don't show anything
    if (!this.props.partner.isCompany && (!this.props.partner.companyId || this.props.partner.companyId <= 0)) {
      return null;
    }
    
    const company = this.state.company as Company;
    
    // If contact is a person with a company, but company data isn't loaded yet or is empty
    if (this.props.partner.companyId && (!company || company.id === -1 || company.id <= 0)) {
      return this.getCompanyEnrichmentAvailableSection();
    }
    
    // Show insights if we have valid company data and it's not the contact itself
    if (company && company.id > 0 && (this.props.partner.isCompany || company.id !== this.props.partner.id)) {
      return this.getCompanyInsightsSection();
    }
    
    return null;
  };

  private openInOdoo = () => {
    if (!this.context.isConnected()) {
      this.context.navigation.goToLogin();
      return;
    }
    const company = this.state.company as Company;
    if (!company || !company.id || company.id < 0) return;
    const companyId = company.id;

    const url = api.baseURL + `/web#id=${companyId}&model=res.partner&view_type=form`;
    openOdooUrl(url);
  };

  render() {
    // Don't show company section if contact is a person without a company attached
    const hasCompanyId = this.props.partner.companyId && typeof this.props.partner.companyId === 'number' && this.props.partner.companyId > 0;
    if (!this.props.partner.isCompany && !hasCompanyId) {
      return null;
    }

    let content = null;

    if (this.state.isLoading) {
      content = <Spinner size={SpinnerSize.large} theme={OdooTheme} style={{ marginBottom: "16px" }} />;
    } else {
      content = this.getCompanySection();
    }

    // Don't render the section if there's no content to show
    if (!content) {
      return null;
    }

    return (
      <CollapseSection
        title={_t("Company Insights")}
        isCollapsed={this.state.isCollapsed}
        hideCollapseButton={this.props.hideCollapseButton}
      >
        {content}
      </CollapseSection>
    );
  }
}

CompanySection.contextType = AppContext;
export default CompanySection;
